/**
 * 节点信息弹窗：查看节点 id / 类型 / 位置尺寸 / data JSON，便于调试与排查。
 * 使用 createPortal 渲染到 document.body。
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy, X } from 'lucide-react';
import { useState } from 'react';
import type { CanvasNode } from './types';
import { getNodeTypeLabel } from './connectionRules';
import toast from '../../components/Toast';

interface NodeInfoDialogProps {
  node: CanvasNode;
  onClose: () => void;
}

export function NodeInfoDialog({ node, onClose }: NodeInfoDialogProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const timer = setTimeout(() => {
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(node.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error('复制失败');
    }
  };

  let dataJson = '';
  try {
    dataJson = JSON.stringify(node.data, null, 2);
  } catch {
    dataJson = String(node.data);
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[10002] flex items-center justify-center bg-slate-950/40 p-4"
      onMouseDown={(e) => {
        if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-950/20"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-800">节点信息 · {getNodeTypeLabel(node.type)}</h3>
          <button
            type="button"
            onClick={onClose}
            className="grid h-7 w-7 place-items-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 px-4 py-3 text-xs">
          <div className="flex items-center gap-2">
            <span className="w-14 shrink-0 text-slate-400">节点 ID</span>
            <code className="min-w-0 flex-1 truncate rounded-md bg-slate-50 px-2 py-1 font-mono text-[11px] text-slate-600">
              {node.id}
            </code>
            <button
              type="button"
              onClick={copyId}
              className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              title="复制 ID"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-14 shrink-0 text-slate-400">位置</span>
            <span className="font-mono text-slate-600">
              x: {Math.round(node.x)}, y: {Math.round(node.y)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-14 shrink-0 text-slate-400">尺寸</span>
            <span className="font-mono text-slate-600">
              {Math.round(node.width)} × {Math.round(node.height)}（zIndex {node.zIndex}）
            </span>
          </div>
          <div>
            <div className="mb-1 text-slate-400">节点数据（data）</div>
            <pre className="max-h-56 overflow-auto rounded-lg bg-slate-50 p-2.5 font-mono text-[11px] leading-relaxed text-slate-600">
              {dataJson}
            </pre>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
